import {Observer} from "mobx-react";
import styled from "styled-components";
import type {RefObject} from "react";
import type {IModulePanelProps} from "../ModuleViewV5";
import type {View} from "../../../../data/models/View";
import {XyiconFeature} from "../../../../generated/api/base";
import type {IViewColumn} from "../../../../data/models/ViewUtils";
import {PopupBodyStyled, PopupHeaderStyled, PopupV5} from "../../popup/PopupV5";
import {ButtonV5} from "../../button/ButtonV5";
import {HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import {baseDistance, fontSize, fontWeight, VerticalFlexStyle} from "../../styles/styles";
import {colorPalette} from "../../styles/colorPalette";
import {TabViewActionBarStyled, TabViewButtonContainerStyled} from "../../widgets/tab/TabViewV5";
import {EditViewColumnsV5} from "./EditViewColumnsV5";

interface IManageColumnsPanelProps extends IModulePanelProps {
	readonly view: View;
	readonly feature: XyiconFeature;
	readonly onClose: () => void;
	readonly parentRef?: RefObject<HTMLDivElement>;
}

export const ManageColumnsPanelV5 = (props: IManageColumnsPanelProps) => {
	const {view, feature, onClose, parentRef} = props;
	const _columns = view.getColumnsToCompare();
	const viewKey = `${feature}-${view?.id ?? "unknown"}`;

	const isResetButtonEnabled = () => {
		return JSON.stringify(_columns) !== view.data.columns;
	};

	const onResetClick = () => {
		const savedColumns = JSON.parse(view.data.columns);

		if (view.itemFeature === XyiconFeature.SpaceEditor) {
			view.setColumns(savedColumns[feature as XyiconFeature.Xyicon | XyiconFeature.Boundary] as IViewColumn[], feature);
		} else {
			view.setColumns(savedColumns as IViewColumn[], feature);
		}
		sessionStorage.setItem(viewKey, "0");
	};

	return (
		<Observer>
			{() => {
				return (
					<ManageColumnsPopupStyled
						onClose={onClose}
						label="Manage Columns"
						className="ManageColumns"
						width="496px"
						height="499px"
						parentRef={parentRef.current}
						noButtons={true}
						verticalAlignment={VerticalAlignment.bottomOuter}
						horizontalAlignment={HorizontalAlignment.left}
						offsetX={0}
						offsetY={8}
						centerOnScreen={parentRef.current === null}
						headerButtons={
							<ButtonV5
								onClick={onResetClick}
								disabled={!isResetButtonEnabled()}
								label="Reset"
								type="secondary"
							/>
						}
					>
						<ManageColumnsPanelStyled className="ManageColumnsPanel CreatePortfolioPanel SidePanel">
							<EditViewColumnsV5
								title=""
								view={view}
								feature={feature}
							/>
						</ManageColumnsPanelStyled>
					</ManageColumnsPopupStyled>
				);
			}}
		</Observer>
	);
};

const ManageColumnsPanelStyled = styled.div`
	${VerticalFlexStyle};
	flex: 1;
	min-height: 0;

	.ColumnEditor {
		border-radius: 0;
		border: none;
		border-top: 1px solid ${colorPalette.gray.c300};

		.manage-cols-box {
			.col {
				&.left {
					background-color: ${colorPalette.gray.c100};
					border-right: 1px solid ${colorPalette.gray.c300};
				}

				&:not(.left) {
					padding-bottom: 0;
				}

				.views {
					.DetailsContent {
						padding: 0;
					}
				}

				${TabViewButtonContainerStyled} {
					padding: 0;
					line-height: inherit;

					${TabViewActionBarStyled} {
						.tabLabel {
							font-size: ${fontSize.md};
							font-weight: ${fontWeight.bold};
							color: ${colorPalette.gray.c950};
							height: 16px;
							line-height: ${baseDistance.md};
						}
					}
				}
			}
		}
	}
`;

const ManageColumnsPopupStyled = styled(PopupV5)`
	${PopupBodyStyled} {
		padding: 0;
	}

	${PopupHeaderStyled} {
		padding: ${baseDistance.sm};

		.name {
			display: flex;
		}
	}
`;
